<template>
  <div ref="rootRef" class="map" @click.capture="onOpenYandexMaps" @auxclick.capture="onOpenYandexMaps">
    <yandex-map
      v-if="isVisible"
      v-model="map"
      class="ymap"
      cursor-grab
      :settings="{
        location: LOCATION,
        showScaleInCopyrights: true,
        behaviors: enabledBehaviors,
      }"
      width="100%"
      height="100%"
    >
      <yandex-map-controls :settings="{ position: 'right' }">
        <yandex-map-zoom-control />
        <yandex-map-geolocation-control />
      </yandex-map-controls>
      <YandexMapDefaultFeaturesLayer />
      <YandexMapDefaultSchemeLayer :settings="{ theme: 'light', visible: false, source: 'scheme' }" />
      <YandexMapLayer :settings="{ zIndex: 1, source: 'scheme', type: 'ground' }" />
      <YandexMapLayer :settings="{ zIndex: 1, source: 'scheme', type: 'labels' }" />
      <YandexMapLayer :settings="{ zIndex: 1, source: 'scheme', type: 'buildings' }" />

      <yandex-map-marker
        position="top-center left-center"
        class="yandex-map-marker"
        :settings="{ coordinates: coordinates }"
      >
        <div class="pin">
          <IconBase name="location-pin" />
        </div>
      </yandex-map-marker>
    </yandex-map>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import {
  YandexMap,
  YandexMapControls,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultSchemeLayer,
  YandexMapGeolocationControl,
  YandexMapLayer,
  YandexMapMarker,
  YandexMapZoomControl,
  initYmaps,
} from 'vue-yandex-maps';

import { IconBase } from '@/shared/ui/iconBase';
import { useLogger } from '@/shared/utils/logger';
import { buildYandexMapsPlaceUrl, isOpenYandexMapsControl, isYandexMapsPlaceUrl } from '@/shared/utils/yandexMapsUrl';
import { syncYmapsStylesToShadowRoot } from '@/shared/utils/ymapsShadowStyles';

import type { BehaviorType, LngLat, YMap, YMapLocationRequest } from '@yandex/ymaps3-types';

const props = withDefaults(
  defineProps<{
    latitude: number;
    longitude: number;
    zoom?: number;
    enabledBehaviors?: BehaviorType[];
  }>(),
  {
    zoom: 15,
    enabledBehaviors: () => ['drag', 'pinchZoom', 'dblClick'],
  },
);

const logger = useLogger();
const shadowRoot = inject<ShadowRoot | null>('shadowRoot', null);

const map = shallowRef<null | YMap>(null);
const rootRef = ref<HTMLElement | null>(null);
const isVisible = ref(false);

let stopStylesSync: (() => void) | null = null;
let stopOpenMapsSync: (() => void) | null = null;

const coordinates = computed<LngLat>(() => [props.longitude, props.latitude]);

const LOCATION = computed<YMapLocationRequest>(() => ({
  center: coordinates.value,
  zoom: props.zoom,
}));

// штатная кнопка Яндекса отдаёт только центр и зум, свой HTML-пин в ссылку не попадает
const yandexMapsUrl = computed(() => buildYandexMapsPlaceUrl(props.longitude, props.latitude, props.zoom));

const hasAttractionPin = (href: string) => {
  try {
    return new URL(href).searchParams.get('pt') === `${props.longitude},${props.latitude}`;
  } catch {
    return false;
  }
};

const applyPinToOpenMapsLinks = () => {
  const root = rootRef.value;

  if (!root) {
    return;
  }

  root.querySelectorAll('a[href]').forEach((node) => {
    const link = node as HTMLAnchorElement;

    if (!isYandexMapsPlaceUrl(link.href) || hasAttractionPin(link.href)) {
      return;
    }

    link.href = yandexMapsUrl.value;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  });
};

const onOpenYandexMaps = (event: MouseEvent) => {
  if (event.button !== 0 && event.button !== 1) {
    return;
  }

  const target = event.target;

  if (!(target instanceof Element) || !isOpenYandexMapsControl(target)) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  window.open(yandexMapsUrl.value, '_blank', 'noopener,noreferrer');
};

watch(yandexMapsUrl, applyPinToOpenMapsLinks);

watch(isVisible, (visible) => {
  stopOpenMapsSync?.();
  stopOpenMapsSync = null;

  if (!visible) {
    return;
  }

  void nextTick(() => {
    const root = rootRef.value;

    if (!root) {
      return;
    }

    applyPinToOpenMapsLinks();

    const observer = new MutationObserver(applyPinToOpenMapsLinks);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
    stopOpenMapsSync = () => {
      observer.disconnect();
    };
  });
});

onMounted(async () => {
  try {
    await initYmaps();

    if (shadowRoot) {
      stopStylesSync = syncYmapsStylesToShadowRoot(shadowRoot);
    }

    isVisible.value = true;
  } catch {
    // ключ Яндекс.Карт привязан к домену: с чужого хоста API отвечает 403 и скрипт не загружается
    logger.error(`Не удалось загрузить Yandex Maps API для ${window.location.origin}`);
  }
});

onBeforeUnmount(() => {
  stopStylesSync?.();
  stopOpenMapsSync?.();
});
</script>

<style scoped lang="scss">
.map {
  width: 100%;
  height: 100%;
}

.ymap {
  background-color: var(--alias-color-skeleton-default);
}

/* stylelint-disable selector-class-pattern -- классы vue-yandex-maps */

// стили vue-yandex-maps подключаются в document и не доходят до shadow DOM,
// без них контейнер карты остаётся нулевой высоты
.map :deep(.__ymap) {
  position: relative;
}

.map :deep(.__ymap_container) {
  width: 100%;
  height: 100%;
}

.map :deep(.__ymap_slots) {
  display: none;
}

// размеры контейнера ymaps3 приходят из внешнего стиля на yastatic.net,
// который грузится асинхронно: без локального дубля карта успевает инициализироваться нулевого размера
.map :deep(.ymaps3--map-container) {
  display: block;
  width: 100%;
  height: 100%;
}

.map :deep(.__ymap--grab [class$='main-engine-container'] canvas) {
  cursor: grab;
}

.map :deep(.__ymap--grabbing:focus [class$='main-engine-container'] canvas),
.map :deep(.__ymap--grabbing:active [class$='main-engine-container'] canvas) {
  cursor: grabbing;
}

.map :deep(.__ymap [class$='graphics-layer']) {
  text-align: left;
}

/* stylelint-enable selector-class-pattern */

.pin {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  // иконка нарисована через currentColor, поэтому цвет задаётся на контейнере
  color: var(--alias-color-icon-inverse);
  cursor: pointer;
  background: var(--alias-color-icon-accent-magenta);
  border: 1px solid var(--alias-color-background-default);
  border-radius: 50%;
}
</style>
